import React, { useMemo, useState } from 'react';
import { Button, ButtonGroup, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AGENT_PRICING_METHOD } from 'arbitrip-common/client/constants/ReservationConstants';
import { getSymbol } from 'arbitrip-common/general/utils/Currencies';
import ClientPriceService from 'arbitrip-common/client/utils/ClientPriceService';
import BookingsActions from '../../../../actions/BookingsActions';
import SettlePaymentReservationsList from './SettlePaymentReservationsList.react';
import { formatAmountsByCurrency, getAggregatedCommissionPercentageText, getAggregatedPricingBreakdown } from './utils';

const PERCENTAGE_DECIMALS = 2;
const DEFAULT_CURRENCY = 'USD';

function isValidAmount(value) {
	const raw = String(value).trim();

	return raw !== '' && !isNaN(raw) && Number(raw) >= 0;
}

function hasDeal(booking) {
	return Boolean(booking?.deal);
}

function getNetPriceTotal(booking) {
	const net_price_total = booking?.deal?.cheapopoPricing?.agentPricing?.net_price_total;

	return Number.isFinite(net_price_total) ? net_price_total : 0;
}

function getSelectionCurrencies(bookings) {
	return [...new Set(bookings.map((booking) => booking?.deal?.cheapopoPricing?.currency).filter(Boolean))];
}

// The absolute amount is the selection's total commission, so each reservation takes the share its
// net price earns. With no net prices to weigh it by, the split is even.
function getAbsoluteShares(bookings, amount) {
	const nets = bookings.map(getNetPriceTotal);
	const total = nets.reduce((sum, net) => sum + net, 0);

	return total > 0 ? nets.map((net) => amount * (net / total)) : bookings.map(() => amount / bookings.length);
}

function getValuePerBooking(bookings, method, value) {
	return method === AGENT_PRICING_METHOD.ABSOLUTE_MARGIN
		? getAbsoluteShares(bookings, value)
		: bookings.map(() => value);
}

// An agent pricing object is deal-specific, so one shared figure still has to be recomputed against
// each reservation's own deal rather than copied across them.
function getAgentPricingForBooking(booking, method, value) {
	const deal = booking?.deal;
	const current = deal?.cheapopoPricing?.agentPricing || deal?.agent_pricing || {};

	return method === AGENT_PRICING_METHOD.ABSOLUTE_MARGIN
		? ClientPriceService.getAgentPricingByAbsoluteMargin(deal, current, value)
		: ClientPriceService.getAgentPricingByMarginPercentage(deal, current, value);
}

// A deal with no net price yields NaN for every derived amount, and committing that wipes the
// reservation's prices. Better to leave Update blocked than to write it.
function isUsableAgentPricing(agentPricing) {
	return Number.isFinite(agentPricing?.client_price_total) && Number.isFinite(agentPricing?.absolute_margin_total);
}

function projectBooking(booking, agentPricing) {
	return {
		...booking,
		deal: {
			...booking?.deal,
			agent_pricing: agentPricing,
			cheapopoPricing: {
				...booking?.deal?.cheapopoPricing,
				agentPricing,
				commissionTotal: agentPricing?.absolute_margin_total,
				cheapopoTotalPrice: agentPricing?.client_price_total,
			},
		},
	};
}

function getPrefillValue(bookings, method) {
	if (!bookings.length) {
		return '';
	}

	if (method === AGENT_PRICING_METHOD.ABSOLUTE_MARGIN) {
		const totals = bookings.map((booking) => booking?.deal?.cheapopoPricing?.agentPricing?.absolute_margin_total);

		return totals.every(Number.isFinite)
			? totals.reduce((sum, total) => sum + total, 0).toFixed(PERCENTAGE_DECIMALS)
			: '';
	}

	const percentages = bookings.map((booking) => booking?.deal?.cheapopoPricing?.agentPricing?.margin_percentage);

	if (!percentages.every(Number.isFinite)) {
		return '';
	}

	const [first] = percentages;

	return percentages.every((percentage) => percentage === first) ? first.toFixed(PERCENTAGE_DECIMALS) : '';
}

const SettlePaymentEditCommissionMulti = ({ bookings, onClose }) => {
	const { t } = useTranslation();

	// A booking with no deal would throw inside the pricing service; everything else is priced the
	// same way single mode prices its one booking.
	const editableBookings = useMemo(() => (bookings || []).filter(hasDeal), [bookings]);

	const currencies = useMemo(() => getSelectionCurrencies(editableBookings), [editableBookings]);
	const isMixedCurrency = currencies.length > 1;
	const currency = currencies.length === 1 ? currencies[0] : DEFAULT_CURRENCY;

	const [selectedMethod, setSelectedMethod] = useState(AGENT_PRICING_METHOD.MARGIN_PERCENTAGE);
	// One amount cannot span two currencies, so a mixed selection is percentage-only.
	const method = isMixedCurrency ? AGENT_PRICING_METHOD.MARGIN_PERCENTAGE : selectedMethod;

	const [pricingValue, setPricingValue] = useState(() =>
		getPrefillValue(editableBookings, AGENT_PRICING_METHOD.MARGIN_PERCENTAGE),
	);

	const valid = isValidAmount(pricingValue);
	const isPercentage = method === AGENT_PRICING_METHOD.MARGIN_PERCENTAGE;

	const handleMethodChange = (nextMethod) => {
		setSelectedMethod(nextMethod);
		setPricingValue(getPrefillValue(editableBookings, nextMethod));
	};

	const pricings = useMemo(() => {
		if (!valid) {
			return null;
		}

		const values = getValuePerBooking(editableBookings, method, Number(pricingValue));

		return editableBookings.map((booking, index) => ({
			booking,
			agent_pricing: getAgentPricingForBooking(booking, method, values[index]),
		}));
	}, [editableBookings, method, pricingValue, valid]);

	const preview = useMemo(
		() =>
			pricings &&
			getAggregatedPricingBreakdown(pricings.map(({ booking, agent_pricing }) => projectBooking(booking, agent_pricing))),
		[pricings],
	);

	const canApply = Boolean(pricings?.length) && pricings.every(({ agent_pricing }) => isUsableAgentPricing(agent_pricing));

	const onUpdateClick = () => {
		if (!canApply) {
			return;
		}

		pricings.forEach(({ booking, agent_pricing }) => {
			BookingsActions.updateAgentPricing(booking, Object.assign({}, agent_pricing, { active: true }));
		});

		onClose();
	};

	const renderAmount = (field) =>
		preview ? formatAmountsByCurrency(preview.totalsByCurrency, preview.currencies, field) : '—';

	return (
		<div className="settle-payment-wrap">
			<div className="settle-payment-edit-commission change-client-price">
				<div className="agent-pricing-container">
					<div className="agent-pricing">
						<div className="edit-pricing">
							<label>{t('SetCommissionMethod')}</label>
							<div className="pricing-method">
								<ButtonGroup aria-label="pricing method" disableElevation color="primary">
									<Button
										value={AGENT_PRICING_METHOD.ABSOLUTE_MARGIN}
										aria-label="absolute commission"
										variant={isPercentage ? 'outlined' : 'contained'}
										onClick={() => handleMethodChange(AGENT_PRICING_METHOD.ABSOLUTE_MARGIN)}
										disabled={isMixedCurrency}
										disableRipple
										className="absolute-margin"
									>
										{t('AbsoluteCommission')} ({getSymbol(currency)})
									</Button>
									<Button
										value={AGENT_PRICING_METHOD.MARGIN_PERCENTAGE}
										aria-label="margin percentage"
										variant={isPercentage ? 'contained' : 'outlined'}
										onClick={() => handleMethodChange(AGENT_PRICING_METHOD.MARGIN_PERCENTAGE)}
										disableRipple
										className="margin-percentage"
									>
										{t('PercentageCommission')} (%)
									</Button>
								</ButtonGroup>
							</div>
							<div className="pricing-value">
								<TextField
									className="pricing-text-field"
									variant="outlined"
									{...(valid ? {} : { error: true, helperText: t('InvalidCommission') })}
									id="pricing-value"
									value={pricingValue}
									onChange={(event) => setPricingValue(event.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												{isPercentage ? '%' : getSymbol(currency)}
											</InputAdornment>
										),
									}}
								/>
							</div>
						</div>
						<div className="pricing-summary">
							<label>{t('Bookings.SettlePayment.PricingSummary')}</label>
							<div className="pricing-item">
								<div className="price-title">{t('Bookings.SettlePayment.TotalNetPrice')}</div>
								<div className="price-value">{renderAmount('total_net_price')}</div>
							</div>
							<div className="pricing-item">
								<div className="price-title">{t('Bookings.SettlePayment.Commission')}</div>
								<div className="price-value">
									{preview
										? getAggregatedCommissionPercentageText(
												preview.totalsByCurrency,
												preview.currencies,
											)
										: '—'}
								</div>
							</div>
							<div className="pricing-item">
								<div className="price-title">{t('Bookings.SettlePayment.TotalCommission')}</div>
								<div className="price-value">{renderAmount('total_commission')}</div>
							</div>
							<div className="pricing-item bold">
								<div className="price-title">{t('Bookings.SettlePayment.TotalClientPrice')}</div>
								<div className="price-value">{renderAmount('total_client_price')}</div>
							</div>
							<div className="pricing-item bold sum">
								<div className="price-title">{t('Bookings.SettlePayment.TotalToPay')}</div>
								<div className="price-value">{renderAmount('total_to_pay')}</div>
							</div>
						</div>
					</div>
				</div>
				<div className="change-client-price-actions">
					<Button autoFocus onClick={onClose} variant="outlined" color="primary" disableRipple>
						{t('Cancel')}
					</Button>
					<Button
						onClick={onUpdateClick}
						variant="contained"
						color="primary"
						disabled={!canApply}
						disableRipple
						className="change-client-price-update"
					>
						{t('Update')}
					</Button>
				</div>
			</div>
			<SettlePaymentReservationsList bookings={bookings} />
		</div>
	);
};

export default SettlePaymentEditCommissionMulti;
